import React, { useState, useEffect } from 'react';
import TemplateManager from '../components/TemplateManager';
import SearchableSelect from '../components/SearchableSelect';
import JsonViewer from '../components/JsonViewer';
import MercantileBraceRateTables from '../components/MercantileBraceRateTables';
import MercantileBraceAlgorithm from '../components/MercantileBraceAlgorithm';
import { calculatePremium, downloadSettingsXlsx, getLatestVersion, getSettings } from '../services/api';
import {
  MB_STATES,
  MB_RATE_TYPES,
  MB_COUNTIES,
  MB_CONSTRUCTION_TYPES,
  MB_OCCUPANCIES,
  MB_PROTECTION_CLASSES,
  MB_DISTANCE_TO_COAST,
  MB_COVERAGE_TYPES,
  MB_PROP_DEDUCTIBLES,
  MB_CAUSE_OF_LOSS,
  MB_WIND_DEDUCTIBLES,
  MB_WIND_DED_OPTIONS,
  MB_SHUTTERS,
  MB_VACANCY,
  MB_COOKING_PBI
} from '../utils/mercantileBraceConstants';
import JSZip from 'jszip';

const POLUNIO_TYPE = '16';

// Ключ подсветки для Wind Base Rate из optionPath движка:
// [lineProperty, WindBaseRate, <state>, tier, ct, dtc, ...] → "WindBaseRate|tier|ct|dtc"
const windKey = (ro) => {
  if (ro && Array.isArray(ro.optionPath)) {
    const tail = ro.optionPath.slice(3);
    if (tail.length >= 3) return 'WindBaseRate|' + tail.slice(0, 3).join('|');
  }
  return null;
};
const covLoss = (c) => c.coverage?.CauseofLoss || c.coverage?.causeOfLoss;

// Заголовок фактора в hint → таблица опций (откуда взята ставка), ключ в _rated.rates,
// section — якорь секции в таблицах ставок (для "?"-скролла),
// key(ctx) — точный ключ строки/ячейки для подсветки (ctx = { building, coverage, ro }).
const FACTOR_SOURCE = {
  'Non-Wind Base Rate': { ratesKey: 'NonWindBaseRate', table: 'NonWindBaseRate', section: 'NonWindBaseRate',
    key: (c) => `NonWindBaseRate|${c.building?.occupancy}|${c.building?.construction}` },
  'Protection Class': { ratesKey: 'protectionClass', table: 'protectionClass', section: 'protectionClass',
    key: (c) => `protectionClass|${c.building?.protectionClass}` },
  'Fire Mod': { ratesKey: 'FireMod_AdvFollExpBldg', table: 'FireMod_AdvFollExpBldg / Cooking_PrcBuildingInhabited', section: 'FireMod', computed: true },
  'Aop Ded Credit': { ratesKey: 'PropDeductible', table: 'PropDeductible', section: 'PropDeductible',
    key: (c) => `PropDeductible|${c.coverage?.deductible}` },
  'Cause Of Loss Factor Non-Wind': { ratesKey: 'CauseOfLossCredit', table: 'CauseOfLossCredit', section: 'protectionClass',
    key: (c) => `CauseOfLossCredit|${covLoss(c)}` },
  'Building Age Non-Wind Crdr': { ratesKey: 'AgeOfBuildingCredits', table: 'AgeOfBuildingCredits', section: 'AgeOfBuildingCredits' },
  'Alarm or Security': { ratesKey: 'AlarmOrSecurity', table: 'PrivateProtectionsCredits', section: 'PrivateProtectionsCredits',
    key: () => 'PrivateProtectionsCredits|AlarmOrSecurity' },
  'Fully Sprinklered': { ratesKey: 'FullySprinklered', table: 'PrivateProtectionsCredits', section: 'PrivateProtectionsCredits',
    key: () => 'PrivateProtectionsCredits|FullySprinklered' },
  'Coverage Rate Adjustment': { ratesKey: 'ALLACovItemTypes2RateMethods', table: 'ALLACovItemTypes2RateMethods' },
  'Fully Updated Property With 10 Years': { ratesKey: 'fullyUpdatedProperty10Years', table: 'fullyUpdatedProperty10Years', section: 'PrivateProtectionsCredits',
    key: () => 'fullyUpdatedProperty10Years|1' },
  'High Crime Debit': { ratesKey: 'HighCrimeDebit', table: 'HighCrimeDebit', section: 'PrivateProtectionsCredits',
    key: () => 'HighCrimeDebit|1' },
  'Loss Free Credit': { ratesKey: 'LossFreeCredit', table: 'LossFreeCredit / LossFreeCreditNotLH', section: 'PrivateProtectionsCredits',
    key: () => 'LossFreeCredit' },
  'Non-Wind Other Credit/Debit Factor': { ratesKey: 'xwindDbCr', table: 'manual: MercCoveragesManualDbCrNonWind', computed: true },
  'Wind Base Rate': { ratesKey: 'WindBaseRate', table: 'WindBaseRate', section: 'WindBaseRate',
    key: (c) => windKey(c.ro) },
  'Cause Of Loss Factor Wind': { ratesKey: 'CauseOfLossCredit', table: 'CauseOfLossCredit', section: 'protectionClass',
    key: (c) => `CauseOfLossCredit|${covLoss(c)}` },
  'Wind Other Credit/Debit Factor': { ratesKey: 'windDbCr', table: 'manual: MercCoveragesManualDbCrWind', computed: true },
  'Wind Deductible Factor': { ratesKey: 'WindHailDeductible', table: 'WindHailDeductible', section: 'WindHailDeductible' },
  'Higher Wind Ded': { ratesKey: 'WindHailDeductible', table: 'WindHailDeductible', section: 'WindHailDeductible' },
  'Named Windstorm Credit/Debit': { ratesKey: 'WindDedOptionDebit', table: 'WindDedOptionDebit', section: 'PropDeductible',
    key: () => 'WindDedOptionDebit|Named Windstorm' },
  'Tri County & Constrction > 1998': { ratesKey: 'WHTriCountyDiscoiunt', table: 'WHTriCountyDiscoiunt' },
  'Roof Replaced': { ratesKey: 'Roofreplaced', table: 'Roofreplaced', section: 'Roofreplaced' },
  'Market Debit': { ratesKey: 'MarketDebit', table: 'MarketDebit' },
  'Building Age Wind Surcharge': { ratesKey: 'BuildingAgeWindSurcharge', table: 'BuildingAgeWindSurcharge', section: 'WindBaseRate' }
};

// Источник ставки: путь в опциях + state group (+ точный optionPath),
// section — якорь таблицы, key — точный ключ опции для подсветки.
const rateSource = (title, rates, ctx) => {
  const src = FACTOR_SOURCE[String(title || '').trim()];
  if (!src) return null;
  const ro = src.ratesKey ? rates?.[src.ratesKey] : null;
  let path = `lineProperty.${src.table}`;
  if (ro && Array.isArray(ro.optionPath)) {
    path = ro.optionPath.join('.');
  }
  let stateSuffix = '';
  if (ro && ro.state) {
    stateSuffix = ro.state === '_all_' ? ' [all states]' : ` [${ro.state}]`;
  }
  let key = null;
  if (src.key && ctx) {
    try {
      const k = src.key({ ...ctx, ro });
      key = k && !/undefined|null/.test(k) ? k : null;
    } catch (e) {
      key = null;
    }
  }
  return { text: path + stateSuffix, computed: !!src.computed, section: src.section || null, key };
};

// Пункт Rating basis → в каких разделах rates используется (+ якоря таблиц для "?")
const BASIS_USAGE = {
  'Coverage type': { label: 'Coverage rate method' },
  'Occupancy': { label: 'Non-Wind Base Rate', sections: ['NonWindBaseRate'] },
  'Construction': { label: 'Non-Wind & Wind Base Rate', sections: ['NonWindBaseRate', 'WindBaseRate'] },
  'Protection Class': { label: 'Protection Class', sections: ['protectionClass'] },
  'Year Built': { label: 'Age of Building, Roof Replaced', sections: ['AgeOfBuildingCredits', 'Roofreplaced'] },
  'Wind Included?': { label: 'Enables Wind rate' },
  'Wind & Hail Deductible': { label: 'Wind Deductible Factor', sections: ['WindHailDeductible'] },
  'County': { label: 'Wind Base Rate (Tier/Zone), Market Debit', sections: ['WindBaseRate'] },
  'Back Yard': { label: 'Wind Base Rate (Back Yard)', sections: ['WindBaseRate'] },
  'Location (Tier)': { label: 'Wind Base Rate (Tier)', sections: ['WindBaseRate'] },
  'Zone': { label: 'Wind Deductible Min, Market Debit', sections: ['WindHailDeductible'] },
  'Proximity To Coast': { label: 'Wind Base Rate (Distance)', sections: ['WindBaseRate'] },
  'Cause Of Loss': { label: 'Cause Of Loss Factor', sections: ['protectionClass'] },
  'Aop Deductible': { label: 'AOP Ded Credit', sections: ['PropDeductible'] }
};

// Продукт официально — только X-Wind Commercial Property (FL/LA).
const PROPERTY_STATES = MB_STATES
  .filter(s => s.value === 'FL' || s.value === 'LA')
  .map(s => ({ value: s.value, label: s.label.replace(' (Property only)', '') }));

const defaultCoverage = () => ({
  type: 'Building',
  limit: 500000,
  deductible: 2500,
  causeOfLoss: 'Basic',
  shuttersCredit: false,
  dbcrNonWind: '',
  dbcrWind: ''
});

const defaultBuilding = () => ({
  name: '',
  construction: 'JM',
  occupancy: 'Mercantile',
  protectionClass: '3',
  yearBuilt: 2005,
  distanceToCoast: '>10 but <30 miles',
  windSelect: true,
  nse: false,
  windDeductible: '5%',
  windDeductibleOption: 'Hurricane',
  shutters: '',
  vacancy: '',
  fireAlarmMonitored: false,
  fireAutomaticFire: false,
  fireSprinkler: false,
  renoPlumbingYear: '',
  renoWiringYear: '',
  renoHeatingYear: '',
  renoRoofingYear: '',
  expShopCarpentry: false,
  expAutoRepair: false,
  expAutoPaint: false,
  expWelding: false,
  expManufacturing: false,
  expCommercialCooking: false,
  cookFryer: false,
  cookGrill: false,
  cookOpenFlame: false,
  cookOther: false,
  cookingPBI: '50% or more',
  coverages: [defaultCoverage()]
});

const defaultLocation = () => ({
  name: '',
  county: 'Duval',
  isMetropolitan: false,
  inspection: false,
  buildings: [defaultBuilding()]
});

const buildDefaultFormData = (effectiveDate, expirationDate) => ({
  policy: {
    state: 'FL',
    rateType: 'NB',
    effectiveDate,
    expirationDate,
    lineProperty: {
      highCrimeArea: false,
      isLossHist: false,
      lossHistoryDateLast: '',
      lossHistoryDateLastAll: '',
      finalPremiumCredit: ''
    },
    triaProperty: false
  },
  locations: [defaultLocation()]
});

const MercantileBrace = () => {
  const [loading, setLoading] = useState(false);
  const [downloadingXlsx, setDownloadingXlsx] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [lastRequest, setLastRequest] = useState(null);
  const [rateTables, setRateTables] = useState(null);
  const [rateTablesLoading, setRateTablesLoading] = useState(false);
  const [showRateTables, setShowRateTables] = useState(false);
  // Подсветка опций в таблицах ставок при клике "?" рядом с полем/фактором
  const [highlight, setHighlight] = useState(null); // { targets:[{section,test}], originId, nonce }
  // Кратковременная подсветка фактора-источника при возврате из таблиц
  const [flashOrigin, setFlashOrigin] = useState(null);

  const today = new Date();
  const nextYear = new Date(today);
  nextYear.setFullYear(nextYear.getFullYear() + 1);
  const formatDate = (date) => date.toISOString().split('T')[0];

  const [versionNumber, setVersionNumber] = useState('1.0.1');
  const [formData, setFormData] = useState(
    buildDefaultFormData(formatDate(today), formatDate(nextYear))
  );

  // Всегда подставляем последнюю доступную версию рейтера
  useEffect(() => {
    let mounted = true;
    getLatestVersion(POLUNIO_TYPE).then(res => {
      if (mounted && res.success && res.data && res.data.number) {
        setVersionNumber(res.data.number);
      }
    });
    return () => { mounted = false; };
  }, []);

  const fmtMoney = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return 'N/A';
    return '$' + num.toLocaleString('en-US', { maximumFractionDigits: 2 });
  };

  // Хинты приходят от API с HTML-разметкой; экранируем всё и возвращаем
  // только известный безопасный набор тегов
  const sanitizeHint = (html) => {
    if (typeof html !== 'string') return '';
    let safe = html
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    safe = safe
      .replace(/&lt;(\/?)(b|strong|em|i|u)&gt;/gi, '<$1$2>')
      .replace(/&lt;br\s*\/?&gt;/gi, '<br>')
      .replace(/&lt;span class='warning'&gt;/gi, '<span class="warning">')
      .replace(/&lt;\/span&gt;/gi, '</span>');
    return safe;
  };

  // ------------------------------------------------------------------
  // Обновление формы
  // ------------------------------------------------------------------

  const updatePolicy = (updates) => {
    setFormData(prev => ({ ...prev, policy: { ...prev.policy, ...updates } }));
  };

  const updatePolicySection = (section, updates) => {
    setFormData(prev => ({
      ...prev,
      policy: {
        ...prev.policy,
        [section]: { ...prev.policy[section], ...updates }
      }
    }));
  };

  // Страховка всегда на год — expiration считается автоматически
  const handleEffectiveDateChange = (effectiveDate) => {
    const date = new Date(effectiveDate);
    date.setFullYear(date.getFullYear() + 1);
    updatePolicy({ effectiveDate, expirationDate: formatDate(date) });
  };

  const updateLocation = (locIndex, updates) => {
    setFormData(prev => {
      const locations = prev.locations.map((loc, i) =>
        i === locIndex ? { ...loc, ...updates } : loc
      );
      return { ...prev, locations };
    });
  };

  const updateBuilding = (locIndex, bldIndex, updates) => {
    setFormData(prev => {
      const locations = prev.locations.map((loc, i) => {
        if (i !== locIndex) return loc;
        const buildings = loc.buildings.map((bld, j) =>
          j === bldIndex ? { ...bld, ...updates } : bld
        );
        return { ...loc, buildings };
      });
      return { ...prev, locations };
    });
  };

  const updateCoverage = (locIndex, bldIndex, covIndex, updates) => {
    setFormData(prev => {
      const locations = prev.locations.map((loc, i) => {
        if (i !== locIndex) return loc;
        const buildings = loc.buildings.map((bld, j) => {
          if (j !== bldIndex) return bld;
          const coverages = bld.coverages.map((cov, k) =>
            k === covIndex ? { ...cov, ...updates } : cov
          );
          return { ...bld, coverages };
        });
        return { ...loc, buildings };
      });
      return { ...prev, locations };
    });
  };

  const addLocation = () => {
    setFormData(prev => ({ ...prev, locations: [...prev.locations, defaultLocation()] }));
  };

  const removeLocation = (locIndex) => {
    setFormData(prev => ({
      ...prev,
      locations: prev.locations.filter((_, i) => i !== locIndex)
    }));
  };

  const addBuilding = (locIndex) => {
    setFormData(prev => {
      const locations = prev.locations.map((loc, i) =>
        i === locIndex ? { ...loc, buildings: [...loc.buildings, defaultBuilding()] } : loc
      );
      return { ...prev, locations };
    });
  };

  const removeBuilding = (locIndex, bldIndex) => {
    setFormData(prev => {
      const locations = prev.locations.map((loc, i) =>
        i === locIndex
          ? { ...loc, buildings: loc.buildings.filter((_, j) => j !== bldIndex) }
          : loc
      );
      return { ...prev, locations };
    });
  };

  const addCoverage = (locIndex, bldIndex) => {
    setFormData(prev => {
      const locations = prev.locations.map((loc, i) => {
        if (i !== locIndex) return loc;
        const buildings = loc.buildings.map((bld, j) =>
          j === bldIndex ? { ...bld, coverages: [...bld.coverages, defaultCoverage()] } : bld
        );
        return { ...loc, buildings };
      });
      return { ...prev, locations };
    });
  };

  const removeCoverage = (locIndex, bldIndex, covIndex) => {
    setFormData(prev => {
      const locations = prev.locations.map((loc, i) => {
        if (i !== locIndex) return loc;
        const buildings = loc.buildings.map((bld, j) =>
          j === bldIndex
            ? { ...bld, coverages: bld.coverages.filter((_, k) => k !== covIndex) }
            : bld
        );
        return { ...loc, buildings };
      });
      return { ...prev, locations };
    });
  };

  const handleLoadTemplate = (templateData) => {
    // Версию из шаблона игнорируем — она всегда остаётся последней доступной
    const { versionNumber: _vn, ...formDataOnly } = templateData;
    setFormData(formDataOnly);
    setResult(null);
    setError(null);
    setLastRequest(null);
  };

  // ------------------------------------------------------------------
  // Формирование запроса к API (формат mercantilebrace__1_0_0.cfc)
  // ------------------------------------------------------------------

  const formatDataForAPI = () => {
    const { policy, locations } = formData;
    const boolNum = (v) => (v ? 1 : 0);

    // account.location: { id1: {..., buildings: { id1: {...} }} }
    const accountLocations = {};
    locations.forEach((loc, locIdx) => {
      const buildings = {};
      loc.buildings.forEach((bld, bldIdx) => {
        buildings['id' + (bldIdx + 1)] = {
          id: String(bldIdx + 1),
          number: String(bldIdx + 1),
          name: bld.name || '',
          construction: bld.construction,
          Occupancy: bld.occupancy,
          protectionClass: bld.protectionClass,
          yearBuilt: parseInt(bld.yearBuilt) || 0,
          distanceToCoast: bld.distanceToCoast,
          WindSelect: boolNum(bld.windSelect),
          NSE: boolNum(bld.nse),
          WindDeductibleoption: bld.windDeductibleOption,
          WindDeductible: bld.windDeductible,
          Shutters: bld.shutters,
          BuildingVacancy: bld.vacancy,
          Cooking_PrcBuildingInhabited: bld.cookingPBI,
          FireSafe_AutomaticAlarmMonitored: boolNum(bld.fireAlarmMonitored),
          FireSafe_AutomaticFire: boolNum(bld.fireAutomaticFire),
          FireSafe_AutomaticSprinkler: boolNum(bld.fireSprinkler),
          RenoPlumbingYear: bld.renoPlumbingYear || '',
          RenoWiringYear: bld.renoWiringYear || '',
          RenoHeatingYear: bld.renoHeatingYear || '',
          RenoRoofingYear: bld.renoRoofingYear || '',
          ExposuresShopCarpentryWoodworking: boolNum(bld.expShopCarpentry),
          ExposuresAutomotiveRepair: boolNum(bld.expAutoRepair),
          ExposuresAutomotivePaintBody: boolNum(bld.expAutoPaint),
          ExposuresWelding: boolNum(bld.expWelding),
          ExposuresManufacturing: boolNum(bld.expManufacturing),
          ExposuresCommercialCooking: boolNum(bld.expCommercialCooking),
          Mini_Cooking_Fryer: boolNum(bld.cookFryer),
          Mini_Cooking_Grill: boolNum(bld.cookGrill),
          Mini_Cooking_OpenFlame: boolNum(bld.cookOpenFlame),
          Mini_Cooking_Other: boolNum(bld.cookOther)
        };
      });

      accountLocations['id' + (locIdx + 1)] = {
        id: String(locIdx + 1),
        number: String(locIdx + 1),
        name: loc.name || '',
        state: policy.state,
        county: loc.county,
        IsMetropolitan: boolNum(loc.isMetropolitan),
        inspection: loc.inspection ? 'yes' : 'no',
        buildings
      };
    });

    const apiPolicy = {
      state: policy.state,
      RateType: policy.rateType,
      effectiveDate: policy.effectiveDate,
      expirationDate: policy.expirationDate
    };

    // -------- lineProperty (Commercial Property, X-Wind) --------
    const risk = {};
    let riskCounter = 0;
    locations.forEach((loc, locIdx) => {
      loc.buildings.forEach((bld, bldIdx) => {
        riskCounter += 1;
        const coverages = {};
        bld.coverages.forEach((cov, covIdx) => {
          coverages['c' + (covIdx + 1)] = {
            type: cov.type,
            limit: parseFloat(cov.limit) || 0,
            deductible: parseInt(cov.deductible) || 0,
            CauseofLoss: cov.causeOfLoss,
            IsShttersCredit: boolNum(cov.shuttersCredit),
            actIsReadOnly: 0,
            MercCoveragesManualDbCrNonWind: cov.dbcrNonWind || '',
            MercCoveragesManualDbCrWind: cov.dbcrWind || ''
          };
        });
        risk['r' + riskCounter] = {
          propertyType: 'Commercial',
          extraData: {
            locationId: locIdx + 1,
            buildingId: bldIdx + 1
          },
          coverages
        };
      });
    });

    const lp = policy.lineProperty;
    apiPolicy.lineProperty = {
      UWIsAnPropCrimeArea: boolNum(lp.highCrimeArea),
      IsLossHist: boolNum(lp.isLossHist),
      LossHistoryDateLast: lp.lossHistoryDateLast || '',
      LossHistoryDateLastAll: lp.lossHistoryDateLastAll || '',
      risk
    };
    if (lp.finalPremiumCredit !== '' && parseFloat(lp.finalPremiumCredit) > 0) {
      apiPolicy.lineProperty.PropertyFinalPremiumCredit = parseFloat(lp.finalPremiumCredit);
    }

    // -------- lineTRIA (только Property terrorism) --------
    if (policy.triaProperty) {
      apiPolicy.lineTRIA = {
        Property: { include: 1 }
      };
    }

    return {
      policy: apiPolicy,
      account: {
        location: accountLocations
      }
    };
  };

  // ------------------------------------------------------------------
  // Отправка запроса
  // ------------------------------------------------------------------

  const doCalculate = async () => {
    const apiData = formatDataForAPI();
    const fullRequest = {
      version: { type: POLUNIO_TYPE, number: versionNumber },
      ...apiData
    };
    setLastRequest(fullRequest);
    const response = await calculatePremium(POLUNIO_TYPE, versionNumber, apiData);
    return { fullRequest, response };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const { response } = await doCalculate();
      if (response.success) {
        if (response.data && response.data._status === 'error') {
          setError(response.data.message + (response.data.where ? ' [' + response.data.where + ']' : ''));
          setResult(response.data);
        } else {
          setResult(response.data);
        }
      } else {
        setError(response.error);
      }
    } catch (err) {
      setError('An unexpected error occurred: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateAndDownload = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    const now = new Date();
    const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);

    try {
      const { fullRequest, response } = await doCalculate();
      const zip = new JSZip();
      zip.file('request.json', JSON.stringify(fullRequest, null, 2));

      const isError = !response.success
        || (response.data && response.data._status === 'error');

      if (response.success) {
        setResult(response.data);
        zip.file('response.json', JSON.stringify(response.data, null, 2));
        if (response.data && response.data._status === 'error') {
          setError(response.data.message);
        }
      } else {
        setError(response.error);
        zip.file('error.json', JSON.stringify({ error: response.error }, null, 2));
      }

      const importTemplate = [{
        id: `calculation-${timestamp}`,
        name: `Calculation ${timestamp}`,
        createdAt: new Date().toISOString(),
        data: { ...formData, versionNumber }
      }];
      zip.file('import.json', JSON.stringify(importTemplate, null, 2));

      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mercantile-brace-calculation${isError ? '-error' : ''}-${timestamp}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('An unexpected error occurred: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadSettingsXlsx = async () => {
    setDownloadingXlsx(true);
    setError(null);
    try {
      const response = await downloadSettingsXlsx(POLUNIO_TYPE, versionNumber);
      if (!response.success) {
        setError(response.error);
      }
    } catch (err) {
      setError('Failed to download settings: ' + err.message);
    } finally {
      setDownloadingXlsx(false);
    }
  };

  // Грузит опции текущей версии, если ещё не загружены или версия сменилась
  const ensureRateTables = async () => {
    if (rateTables && rateTables.version === versionNumber) return true;
    setRateTablesLoading(true);
    setError(null);
    try {
      const response = await getSettings(POLUNIO_TYPE, versionNumber);
      if (response.success) {
        setRateTables({ version: versionNumber, data: response.data });
        return true;
      }
      setError(response.error);
      return false;
    } catch (err) {
      setError('Failed to load rate tables: ' + err.message);
      return false;
    } finally {
      setRateTablesLoading(false);
    }
  };

  const handleToggleRateTables = async () => {
    if (showRateTables) {
      setShowRateTables(false);
      return;
    }
    setShowRateTables(true);
    await ensureRateTables();
  };

  // Цель подсветки: { section, test?(cellKey)->bool }.
  // test задан → подсвечиваются конкретные ячейки; нет test → только скролл к секции.
  const exactT = (section, key) => ({ section, test: (k) => k === key });
  // section-level: подсвечивает секцию целиком (совпадает с синтетическим ключом секции)
  const sectionT = (section) => ({ section, test: (k) => k === 'section|' + section });
  // Все ячейки Wind Base Rate для данной конструкции (колонка по всем tier/дистанциям)
  const windConstrT = (con) => ({
    section: 'WindBaseRate',
    test: (k) => k.startsWith('WindBaseRate|') && k.split('|')[2] === con
  });

  // "?" рядом с полем/фактором: показать таблицы ставок, доскроллить и подсветить
  // опцию во ВСЕХ таблицах, где это значение используется (targets — массив целей)
  const showAndHighlight = async (targets, originId) => {
    const list = (Array.isArray(targets) ? targets : [targets]).filter((t) => t && t.section);
    if (!list.length) return;
    setShowRateTables(true);
    setHighlight((h) => ({ targets: list, originId, nonce: (h?.nonce || 0) + 1 }));
    await ensureRateTables();
  };

  // Возврат из таблиц ставок к фактору-источнику
  const backToResult = () => {
    const originId = highlight?.originId;
    setHighlight(null);
    if (!originId) return;
    setFlashOrigin(originId);
    setTimeout(() => {
      const el = document.getElementById(originId);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 30);
    setTimeout(() => setFlashOrigin(null), 1800);
  };

  // Скролл к первой подсвеченной опции (или к первой секции), когда таблицы отрисованы
  useEffect(() => {
    if (!highlight) return;
    const targets = highlight.targets || [];
    const match = (k) => targets.some((t) => t.test && t.test(k));
    let tries = 0;
    let timer = null;
    const tryScroll = () => {
      let el = Array.from(document.querySelectorAll('[data-mbkey]'))
        .find((n) => match(n.getAttribute('data-mbkey'))) || null;
      if (!el) {
        for (const t of targets) {
          el = document.getElementById('mbrt-' + t.section);
          if (el) break;
        }
      }
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (tries++ < 25) {
        timer = setTimeout(tryScroll, 100);
      }
    };
    timer = setTimeout(tryScroll, 60);
    return () => timer && clearTimeout(timer);
  }, [highlight]);

  // ------------------------------------------------------------------
  // Рендеринг результатов
  // ------------------------------------------------------------------

  // Значение элемента hint для отображения
  const hintValue = (item) => {
    const v = item?.valueDisplay?.value !== undefined ? item.valueDisplay.value : item?.value;
    if (v === undefined || v === null) return '';
    return typeof v === 'object' ? JSON.stringify(v) : String(v);
  };

  // Разбор hint на смысловые группы: характеристики объекта, non-wind, wind, итог
  const parseHint = (hint) => {
    const groups = { header: '', info: [], nonWind: [], wind: [], footer: [] };
    if (!Array.isArray(hint)) return groups;
    let cur = 'info';
    for (const item of hint) {
      if (item.center === 'Non-Wind Coverage') { cur = 'nonWind'; continue; }
      if (item.center === 'Wind Coverage') { cur = 'wind'; continue; }
      if (item.break) { cur = 'footer'; continue; }
      if (item.center) { groups.header = item.center; continue; }
      groups[cur].push(item);
    }
    return groups;
  };

  const formulaStyle = {
    fontFamily: 'monospace',
    fontSize: '1em',
    background: '#f8f9fa',
    border: '1px solid #e9ecef',
    borderRadius: '4px',
    padding: '6px 10px',
    margin: '6px 0'
  };

  const sourceBtnStyle = {
    marginLeft: '6px',
    width: '18px',
    height: '18px',
    lineHeight: '16px',
    padding: 0,
    borderRadius: '50%',
    border: '1px solid #0d6efd',
    background: '#e7f1ff',
    color: '#0d6efd',
    fontWeight: 'bold',
    fontSize: '0.8em',
    cursor: 'pointer',
    verticalAlign: 'middle'
  };

  // Метка поля формы + "?" со ссылкой на таблицы ставок, на которые влияет это поле.
  // targets — массив целей { section, test? }; подсвечивается опция во всех этих таблицах.
  const labelWithRate = (text, fieldId, targets) => {
    const list = (targets || []).filter((t) => t && t.section);
    return (
      <label
        id={fieldId}
        style={{
          scrollMarginTop: '90px',
          ...(flashOrigin === fieldId ? { background: '#fff3b0', borderRadius: '3px' } : null)
        }}
      >
        {text}
        {list.length > 0 && (
          <button
            type="button"
            onClick={() => showAndHighlight(list, fieldId)}
            title="Show the rate table(s) this field affects"
            style={sourceBtnStyle}
          >
            ?
          </button>
        )}
      </label>
    );
  };

  // Чекбокс + "?" со ссылкой на таблицу ставок (кнопка вне label, чтобы не переключать чекбокс)
  const checkboxWithRate = (text, checked, onChange, fieldId, targets, spaced) => {
    const list = (targets || []).filter((t) => t && t.section);
    return (
      <div
        id={fieldId}
        style={{
          display: 'flex',
          alignItems: 'center',
          scrollMarginTop: '90px',
          ...(spaced ? { marginTop: '4px' } : null),
          ...(flashOrigin === fieldId ? { background: '#fff3b0', borderRadius: '3px' } : null)
        }}
      >
        <label style={{ fontWeight: 'normal', margin: 0 }}>
          <input type="checkbox" checked={checked} onChange={onChange} />{' '}
          {text}
        </label>
        {list.length > 0 && (
          <button
            type="button"
            onClick={() => showAndHighlight(list, fieldId)}
            title="Show the rate table this field affects"
            style={sourceBtnStyle}
          >
            ?
          </button>
        )}
      </div>
    );
  };

  // Компактная сетка Rating basis: пары «характеристика: значение» в 2–3 колонки,
  // у каждого пункта — в каком разделе rates используется (+ "?" к таблице)
  const renderRatingBasis = (rows, idBase) => (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      columnGap: '24px',
      fontSize: '0.9em'
    }}>
      {rows.map((item, idx) => {
        const usage = BASIS_USAGE[String(item.title || '').trim()];
        const originId = `${idBase}-b${idx}`;
        const flashed = flashOrigin === originId;
        return (
          <div
            key={idx}
            id={originId}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              gap: '10px',
              padding: '2px 6px',
              borderBottom: '1px solid #f0f0f0',
              scrollMarginTop: '90px',
              ...(flashed ? { background: '#fff3b0', transition: 'background 0.4s' } : null)
            }}
          >
            <div style={{ minWidth: 0 }}>
              <span style={{ color: '#555' }}>{item.title}</span>
              {item.desc !== undefined && item.desc !== '' && (
                <span style={{ color: '#aaa', marginLeft: '6px', fontSize: '0.85em' }}>
                  ({typeof item.desc === 'object' ? JSON.stringify(item.desc) : item.desc})
                </span>
              )}
              {usage && (
                <span style={{ display: 'block', color: '#9aa', fontSize: '0.78em', lineHeight: '1.3' }}>
                  used in: {usage.label}
                  {usage.sections && usage.sections.length > 0 && (
                    <button
                      type="button"
                      onClick={() => showAndHighlight(usage.sections.map(sectionT), originId)}
                      title={`Open the ${usage.label} rate table`}
                      style={{ ...sourceBtnStyle, width: '15px', height: '15px', lineHeight: '13px', fontSize: '0.72em', marginLeft: '4px' }}
                    >
                      ?
                    </button>
                  )}
                </span>
              )}
            </div>
            <div style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{hintValue(item)}</div>
          </div>
        );
      })}
    </div>
  );

  // Секция ставки: база × факторы = итоговая ставка, затем ставка / 100 × лимит = премия.
  // У каждого фактора указывается источник (из какой таблицы опций взята ставка).
  const renderRateBlock = (label, rows, limit, premium, rates, ctx, blockKey, extraHint) => {
    if (!rows || rows.length === 0) return null;
    const finalRow = rows.find(r => /^Final/i.test(r.title || '')) || rows[rows.length - 1];
    const components = rows.filter(r => r !== finalRow);
    const finalVal = hintValue(finalRow);
    const premiumLabel = `${label.replace(/ rate$/i, '')} premium`;

    // Размеченный член формулы: приглушённая подпись (что это) + жирное значение
    const fLabel = { color: '#6b7280', marginRight: '4px' };
    const fVal = { fontWeight: 'bold', color: '#2c3e50' };
    const fOp = { color: '#adb5bd', margin: '0 3px' };
    const term = (lab, val, k) => (
      <span key={k} style={{ whiteSpace: 'nowrap' }}>
        <span style={fLabel}>{lab}</span><span style={fVal}>{val}</span>
      </span>
    );
    const formulaRow = {
      ...formulaStyle,
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'baseline',
      gap: '2px 4px',
      fontFamily: 'inherit'
    };

    return (
      <div style={{ marginTop: '10px' }}>
        <h5 style={{ margin: '4px 0' }}>{label}</h5>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #ccc', color: '#888', fontSize: '0.85em' }}>
              <td style={{ padding: '3px 10px' }}>Factor</td>
              <td style={{ padding: '3px 10px' }}>Source (options table)</td>
              <td style={{ padding: '3px 10px', textAlign: 'right' }}>Value</td>
            </tr>
          </thead>
          <tbody>
            {components.map((item, idx) => {
              const src = rateSource(item.title, rates, ctx);
              const originId = `mbsrc-${blockKey}-${idx}`;
              const flashed = flashOrigin === originId;
              return (
                <tr
                  key={idx}
                  id={originId}
                  style={{
                    borderBottom: '1px solid #eee',
                    scrollMarginTop: '90px',
                    ...(flashed ? { background: '#fff3b0', transition: 'background 0.4s' } : null)
                  }}
                >
                  <td style={{ padding: '3px 10px' }}>
                    {item.title}
                    {item.desc !== undefined && item.desc !== '' && (
                      <span style={{ color: '#888', marginLeft: '6px', fontSize: '0.85em' }}>
                        ({typeof item.desc === 'object' ? JSON.stringify(item.desc) : item.desc})
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '3px 10px', fontFamily: 'monospace', fontSize: '1em', color: '#556' }}>
                    {src ? src.text : ''}
                    {src?.computed && <span style={{ color: '#999' }}> (computed)</span>}
                    {src?.section && (
                      <button
                        type="button"
                        onClick={() => showAndHighlight([src.key ? exactT(src.section, src.key) : sectionT(src.section)], originId)}
                        title="Show the exact rate table and highlight this option"
                        style={sourceBtnStyle}
                      >
                        ?
                      </button>
                    )}
                  </td>
                  <td style={{ padding: '3px 10px', textAlign: 'right' }}>{hintValue(item)}</td>
                </tr>
              );
            })}
            <tr style={{ borderTop: '1px solid #adb5bd' }}>
              <td style={{ padding: '3px 10px', fontWeight: 'bold' }} colSpan="2">{finalRow.title}</td>
              <td style={{ padding: '3px 10px', textAlign: 'right', fontWeight: 'bold', color: '#28a745' }}>
                {finalVal}
              </td>
            </tr>
          </tbody>
        </table>
        <div style={formulaRow}>
          {components.length > 0 && components.map((item, i) => (
            <React.Fragment key={`c${i}`}>
              {i > 0 && <span style={fOp}>×</span>}
              {term(item.title, hintValue(item), `t${i}`)}
            </React.Fragment>
          ))}
          {components.length > 0 && <span style={fOp}>=</span>}
          {term(finalRow.title, finalVal, 'final')}
          <span style={{ color: '#8a94a6', fontSize: '0.78em', marginLeft: '4px' }}>(rate per $100)</span>
        </div>
        <div style={formulaRow}>
          {term(finalRow.title, finalVal, 'r2')}
          <span style={fOp}>/ 100 ×</span>
          {term('Limit', fmtMoney(limit), 'lim')}
          <span style={fOp}>=</span>
          {term(premiumLabel, fmtMoney(premium), 'prem')}
        </div>
        {extraHint && (
          <div style={{ ...formulaStyle, color: '#666' }}>
            <span dangerouslySetInnerHTML={{ __html: sanitizeHint(extraHint) }} />
          </div>
        )}
      </div>
    );
  };

  const renderCoverageBreakdown = (cov, risk, keyId) => {
    const r = cov._rated;
    if (!r) return null;
    const g = parseHint(r.hint);
    const hasWind = g.wind.length > 0 || parseFloat(r.premiumWind) > 0;
    // Контекст для источника ставок: здание (по extraData) + покрытие
    const locIdx = (risk.extraData?.locationId || 1) - 1;
    const bldIdx = (risk.extraData?.buildingId || 1) - 1;
    const building = formData.locations?.[locIdx]?.buildings?.[bldIdx] || null;
    const ctx = { building, coverage: cov };

    return (
      <details key={keyId} open style={{ marginBottom: '12px', border: '1px solid #dee2e6', borderRadius: '6px' }}>
        <summary style={{ cursor: 'pointer', padding: '10px', background: '#e9ecef', borderRadius: '6px', fontWeight: '500' }}>
          Location {risk.extraData?.locationId} / Building {risk.extraData?.buildingId} — {cov.type}:
          {' '}<strong style={{ color: '#28a745' }}>{fmtMoney(r.premium)}</strong>
          {' '}(limit {fmtMoney(cov.limit)})
        </summary>
        <div style={{ padding: '12px' }}>
          {g.info.length > 0 && (
            <>
              <h5 style={{ margin: '4px 0' }}>Rating basis</h5>
              {renderRatingBasis(g.info, keyId + '-basis')}
            </>
          )}

          {renderRateBlock('Non-Wind rate', g.nonWind, cov.limit, r.premiumXWind, r.rates, ctx, keyId + '-nw')}
          {hasWind && renderRateBlock('Wind rate', g.wind, cov.limit, r.premiumWind, r.rates, ctx, keyId + '-w', r.premiumWind_Hint)}

          <div style={{
            marginTop: '12px',
            padding: '10px',
            background: '#eaf7ee',
            border: '1px solid #b7e4c7',
            borderRadius: '4px'
          }}>
            <div><strong>Combined rate:</strong> {r.finalRate} per $100</div>
            <div style={{ ...formulaStyle, background: '#fff' }}>
              {`Non-Wind ${fmtMoney(r.premiumXWind)}`}
              {hasWind ? ` + Wind ${fmtMoney(r.premiumWind)}` : ''}
              {`  →  Coverage premium ${fmtMoney(r.premium)}`}
            </div>
          </div>
        </div>
      </details>
    );
  };

  const renderPropertyResults = () => {
    const lineProperty = result?.policy?.lineProperty;
    if (!lineProperty?._rated) return null;
    const risks = lineProperty.risk || {};
    const lp = lineProperty._rated;
    const coveragePremiumSum = Object.keys(risks).reduce((sum, riskKey) => {
      const cov = risks[riskKey]?.coverages || {};
      return sum + Object.keys(cov).reduce((s, k) => s + (parseFloat(cov[k]?._rated?.premium) || 0), 0);
    }, 0);

    return (
      <div className="card">
        <h3>Commercial Property — {fmtMoney(lp.premium)}</h3>
        <div style={{ ...formulaStyle }}>
          <div>Sum of coverage premiums: {fmtMoney(coveragePremiumSum)}</div>
          {parseFloat(lp.premiumMinimum) > 0 && (
            <div>+ Minimum property premium adjustment: {fmtMoney(lp.premiumMinimum)}</div>
          )}
          {parseFloat(lp.premiumCoDiscount) !== 0 && !isNaN(parseFloat(lp.premiumCoDiscount)) && (
            <div>+ Final premium credit: {fmtMoney(lp.premiumCoDiscount)}</div>
          )}
          <div style={{ fontWeight: 'bold', marginTop: '4px' }}>= Property premium: {fmtMoney(lp.premium)}</div>
        </div>

        {Object.keys(risks).map(riskKey => {
          const risk = risks[riskKey];
          if (!risk?.coverages) return null;
          return Object.keys(risk.coverages).map(covKey => {
            const cov = risk.coverages[covKey];
            if (!cov?._rated) return null;
            return renderCoverageBreakdown(cov, risk, riskKey + covKey);
          });
        })}
      </div>
    );
  };

  const renderTRIAResults = () => {
    const lineTRIA = result?.policy?.lineTRIA;
    if (!lineTRIA?._rated) return null;

    return (
      <div className="card">
        <h3>TRIA (Terrorism) — {fmtMoney(lineTRIA._rated.premium)}</h3>
        {Object.keys(lineTRIA).map(key => {
          if (key === '_rated') return null;
          const line = lineTRIA[key];
          if (!line?._rated) return null;
          const triaOriginId = `mbtria-${key}`;
          const triaFlashed = flashOrigin === triaOriginId;
          return (
            <div
              key={key}
              id={triaOriginId}
              style={{
                marginBottom: '10px',
                scrollMarginTop: '90px',
                ...(triaFlashed ? { background: '#fff3b0', transition: 'background 0.4s' } : null)
              }}
            >
              <p>
                <strong>{key}:</strong> {line._rated.tbd ? 'TBD' : fmtMoney(line._rated.premium)}
                {parseFloat(line._rated.premiumMinimum) > 0 && (
                  <span className="text-muted"> (incl. min premium adj. {fmtMoney(line._rated.premiumMinimum)})</span>
                )}
                <button
                  type="button"
                  onClick={() => showAndHighlight([sectionT('lineTRIA')], triaOriginId)}
                  title="Show the TRIA (Terrorism) rate table"
                  style={sourceBtnStyle}
                >
                  ?
                </button>
              </p>
              {Array.isArray(line._rated.HintRates) && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
                  <tbody>
                    {line._rated.HintRates.map((h, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '3px 10px' }}>
                          <span dangerouslySetInnerHTML={{ __html: sanitizeHint(h.title) }} />
                        </td>
                        <td style={{ padding: '3px 10px', textAlign: 'right', fontWeight: '500' }}>
                          <span dangerouslySetInnerHTML={{ __html: sanitizeHint(String(h.value)) }} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderSummary = () => {
    const rated = result?.policy?._rated;
    if (!rated) return null;

    const propertyPremium = result?.policy?.lineProperty?._rated?.premium;
    const triaPremium = result?.policy?.lineTRIA?._rated?.premium;
    const totalPremium = parseFloat(rated.premium) || 0;
    const totalFee = parseFloat(rated.fee) || 0;
    const grandTotal = totalPremium + totalFee;

    const rowStyle = { padding: '4px 0', borderBottom: '1px solid #d6e6f5' };
    const labelStyle = { display: 'inline-block', minWidth: '260px' };

    return (
      <div className="card" style={{ backgroundColor: '#e7f3ff', border: '1px solid #b3d7ff' }}>
        <h3>Summary — how the total is calculated</h3>

        <div style={{ fontSize: '1.02em' }}>
          {propertyPremium !== undefined && (
            <div style={rowStyle}>
              <span style={labelStyle}>Commercial Property premium</span>
              <strong>{fmtMoney(propertyPremium)}</strong>
            </div>
          )}
          {triaPremium !== undefined && (
            <div style={rowStyle}>
              <span style={labelStyle}>TRIA (Terrorism) premium</span>
              <strong>{fmtMoney(triaPremium)}</strong>
            </div>
          )}
          <div style={{ ...rowStyle, borderBottom: '2px solid #7fb2e0' }}>
            <span style={labelStyle}><strong>Total Premium</strong></span>
            <strong style={{ color: '#28a745' }}>{fmtMoney(rated.premium)}</strong>
          </div>

          {rated.policyfee?.value !== undefined && (
            <div style={rowStyle}>
              <span style={labelStyle}>
                Policy Fee
                {rated.rates?.policyfee?.type && (
                  <span className="text-muted"> ({rated.rates.policyfee.type})</span>
                )}
              </span>
              <strong>{fmtMoney(rated.policyfee.value)}</strong>
            </div>
          )}
          {rated.inspection !== undefined && (
            <div style={rowStyle}>
              <span style={labelStyle}>Inspection Fees</span>
              <strong>{fmtMoney(rated.inspection)}</strong>
            </div>
          )}
          {rated.fee !== undefined && (
            <div style={{ ...rowStyle, borderBottom: '2px solid #7fb2e0' }}>
              <span style={labelStyle}><strong>Total Fees</strong></span>
              <strong>{fmtMoney(rated.fee)}</strong>
            </div>
          )}

          <div style={{ padding: '8px 0', fontSize: '1.25em' }}>
            <span style={labelStyle}><strong>Grand Total (Premium + Fees)</strong></span>
            <strong style={{ color: '#28a745' }}>{fmtMoney(grandTotal)}</strong>
          </div>
        </div>
      </div>
    );
  };

  // ------------------------------------------------------------------
  // Формы
  // ------------------------------------------------------------------

  const countyOptions = (MB_COUNTIES[formData.policy.state] || MB_COUNTIES.FL)
    .map(c => ({ value: c, label: c }));

  const renderCoverageForm = (cov, locIndex, bldIndex, covIndex) => (
    <div
      key={covIndex}
      style={{
        border: '1px solid #ced4da',
        borderRadius: '4px',
        padding: '10px',
        marginBottom: '10px',
        backgroundColor: '#fdfdfd'
      }}
    >
      <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <strong>Coverage {covIndex + 1}</strong>
        <button
          type="button"
          className="btn btn-danger"
          style={{ padding: '2px 10px' }}
          onClick={() => removeCoverage(locIndex, bldIndex, covIndex)}
        >
          Remove
        </button>
      </div>
      <div className="grid">
        <div className="form-group">
          <label>Type</label>
          <SearchableSelect
            options={MB_COVERAGE_TYPES}
            value={cov.type}
            onChange={(value) => updateCoverage(locIndex, bldIndex, covIndex, { type: value })}
            required
          />
        </div>
        <div className="form-group">
          <label>Limit</label>
          <input
            type="number"
            className="form-control"
            value={cov.limit}
            min="0"
            onChange={(e) => updateCoverage(locIndex, bldIndex, covIndex, { limit: e.target.value })}
            required
          />
        </div>
        <div className="form-group">
          {labelWithRate('AOP Deductible', `mbfld-${locIndex}-${bldIndex}-c${covIndex}-ded`, [exactT('PropDeductible', `PropDeductible|${cov.deductible}`)])}
          <select
            className="form-control"
            value={cov.deductible}
            onChange={(e) => updateCoverage(locIndex, bldIndex, covIndex, { deductible: parseInt(e.target.value) })}
          >
            {MB_PROP_DEDUCTIBLES.map(d => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          {labelWithRate('Cause of Loss', `mbfld-${locIndex}-${bldIndex}-c${covIndex}-col`, [exactT('protectionClass', `CauseOfLossCredit|${cov.causeOfLoss}`)])}
          <select
            className="form-control"
            value={cov.causeOfLoss}
            onChange={(e) => updateCoverage(locIndex, bldIndex, covIndex, { causeOfLoss: e.target.value })}
          >
            {MB_CAUSE_OF_LOSS.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Manual Db/Cr Non-Wind</label>
          <input
            type="number"
            step="0.01"
            className="form-control"
            value={cov.dbcrNonWind}
            placeholder="1.0 = none"
            onChange={(e) => updateCoverage(locIndex, bldIndex, covIndex, { dbcrNonWind: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label>Manual Db/Cr Wind</label>
          <input
            type="number"
            step="0.01"
            className="form-control"
            value={cov.dbcrWind}
            placeholder="1.0 = none"
            onChange={(e) => updateCoverage(locIndex, bldIndex, covIndex, { dbcrWind: e.target.value })}
          />
        </div>
      </div>
      <div className="form-group">
        <label style={{ fontWeight: 'normal' }}>
          <input
            type="checkbox"
            checked={cov.shuttersCredit}
            onChange={(e) => updateCoverage(locIndex, bldIndex, covIndex, { shuttersCredit: e.target.checked })}
          />{' '}
          Apply Shutters Credit
        </label>
      </div>
    </div>
  );

  const renderBuildingForm = (bld, locIndex, bldIndex) => (
    <div
      key={bldIndex}
      style={{
        border: '1px solid #adb5bd',
        borderRadius: '6px',
        padding: '12px',
        marginBottom: '12px',
        backgroundColor: '#f8f9fa'
      }}
    >
      <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <h4>Building {bldIndex + 1}</h4>
        <button
          type="button"
          className="btn btn-danger"
          style={{ padding: '2px 10px' }}
          onClick={() => removeBuilding(locIndex, bldIndex)}
        >
          Remove Building
        </button>
      </div>

      <div className="grid">
        <div className="form-group">
          <label>Name</label>
          <input
            type="text"
            className="form-control"
            value={bld.name}
            onChange={(e) => updateBuilding(locIndex, bldIndex, { name: e.target.value })}
          />
        </div>
        <div className="form-group">
          {labelWithRate('Construction', `mbfld-${locIndex}-${bldIndex}-construction`, [exactT('NonWindBaseRate', `NonWindBaseRate|${bld.occupancy}|${bld.construction}`), windConstrT(bld.construction)])}
          <select
            className="form-control"
            value={bld.construction}
            onChange={(e) => updateBuilding(locIndex, bldIndex, { construction: e.target.value })}
          >
            {MB_CONSTRUCTION_TYPES.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          {labelWithRate('Occupancy', `mbfld-${locIndex}-${bldIndex}-occupancy`, [exactT('NonWindBaseRate', `NonWindBaseRate|${bld.occupancy}|${bld.construction}`)])}
          <select
            className="form-control"
            value={bld.occupancy}
            onChange={(e) => updateBuilding(locIndex, bldIndex, { occupancy: e.target.value })}
          >
            {MB_OCCUPANCIES.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          {labelWithRate('Protection Class', `mbfld-${locIndex}-${bldIndex}-pc`, [exactT('protectionClass', `protectionClass|${bld.protectionClass}`)])}
          <select
            className="form-control"
            value={bld.protectionClass}
            onChange={(e) => updateBuilding(locIndex, bldIndex, { protectionClass: e.target.value })}
          >
            {MB_PROTECTION_CLASSES.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          {labelWithRate('Year Built', `mbfld-${locIndex}-${bldIndex}-year`, [sectionT('AgeOfBuildingCredits'), sectionT('Roofreplaced')])}
          <input
            type="number"
            className="form-control"
            value={bld.yearBuilt}
            min="1800"
            max="2100"
            onChange={(e) => updateBuilding(locIndex, bldIndex, { yearBuilt: e.target.value })}
            required
          />
        </div>
        <div className="form-group">
          {labelWithRate('Distance to Coast', `mbfld-${locIndex}-${bldIndex}-dtc`, [sectionT('WindBaseRate')])}
          <select
            className="form-control"
            value={bld.distanceToCoast}
            onChange={(e) => updateBuilding(locIndex, bldIndex, { distanceToCoast: e.target.value })}
          >
            {MB_DISTANCE_TO_COAST.map(d => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          {labelWithRate('Building Vacancy', `mbfld-${locIndex}-${bldIndex}-vacancy`, [exactT('BuildingVacancy', `BuildingVacancy|${bld.vacancy}`), sectionT('BuildingVacancy')])}
          <select
            className="form-control"
            value={bld.vacancy}
            onChange={(e) => updateBuilding(locIndex, bldIndex, { vacancy: e.target.value })}
          >
            {MB_VACANCY.map(v => (
              <option key={v.value} value={v.value}>{v.label}</option>
            ))}
          </select>
        </div>
      </div>

      <h5 style={{ marginTop: '10px' }}>Wind</h5>
      <div className="grid">
        <div className="form-group">
          <label style={{ fontWeight: 'normal' }}>
            <input
              type="checkbox"
              checked={bld.windSelect}
              onChange={(e) => updateBuilding(locIndex, bldIndex, { windSelect: e.target.checked })}
            />{' '}
            Wind Included
          </label>
          <label style={{ fontWeight: 'normal', display: 'block' }}>
            <input
              type="checkbox"
              checked={bld.nse}
              onChange={(e) => updateBuilding(locIndex, bldIndex, { nse: e.target.checked })}
            />{' '}
            NSE (Named Storm Endorsement)
          </label>
        </div>
        <div className="form-group">
          {labelWithRate('Wind Deductible', `mbfld-${locIndex}-${bldIndex}-wd`, [sectionT('WindHailDeductible')])}
          <select
            className="form-control"
            value={bld.windDeductible}
            onChange={(e) => updateBuilding(locIndex, bldIndex, { windDeductible: e.target.value })}
          >
            {MB_WIND_DEDUCTIBLES.map(d => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Wind Deductible Option</label>
          <select
            className="form-control"
            value={bld.windDeductibleOption}
            onChange={(e) => updateBuilding(locIndex, bldIndex, { windDeductibleOption: e.target.value })}
          >
            {MB_WIND_DED_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          {labelWithRate('Shutters', `mbfld-${locIndex}-${bldIndex}-shut`, [sectionT('ShuttersCredit')])}
          <select
            className="form-control"
            value={bld.shutters}
            onChange={(e) => updateBuilding(locIndex, bldIndex, { shutters: e.target.value })}
          >
            {MB_SHUTTERS.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      <h5 style={{ marginTop: '10px' }}>Fire Protection & Renovations</h5>
      <div className="grid">
        <div className="form-group">
          <label>Fire Protection</label>
          {checkboxWithRate(
            'Automatic Alarm (Monitored)',
            bld.fireAlarmMonitored,
            (e) => updateBuilding(locIndex, bldIndex, { fireAlarmMonitored: e.target.checked }),
            `mbfld-${locIndex}-${bldIndex}-alarm`,
            [exactT('PrivateProtectionsCredits', 'PrivateProtectionsCredits|AlarmOrSecurity')]
          )}
          {checkboxWithRate(
            'Automatic Fire Alarm',
            bld.fireAutomaticFire,
            (e) => updateBuilding(locIndex, bldIndex, { fireAutomaticFire: e.target.checked }),
            `mbfld-${locIndex}-${bldIndex}-firealarm`,
            [exactT('PrivateProtectionsCredits', 'PrivateProtectionsCredits|AlarmOrSecurity')],
            true
          )}
          {checkboxWithRate(
            'Fully Sprinklered',
            bld.fireSprinkler,
            (e) => updateBuilding(locIndex, bldIndex, { fireSprinkler: e.target.checked }),
            `mbfld-${locIndex}-${bldIndex}-sprinkler`,
            [exactT('PrivateProtectionsCredits', 'PrivateProtectionsCredits|FullySprinklered')],
            true
          )}
        </div>
        <div className="form-group">
          {labelWithRate('Reno: Plumbing Year', `mbfld-${locIndex}-${bldIndex}-renoplumb`, [exactT('PrivateProtectionsCredits', 'fullyUpdatedProperty10Years|1')])}
          <input
            type="number"
            className="form-control"
            value={bld.renoPlumbingYear}
            onChange={(e) => updateBuilding(locIndex, bldIndex, { renoPlumbingYear: e.target.value })}
          />
        </div>
        <div className="form-group">
          {labelWithRate('Reno: Wiring Year', `mbfld-${locIndex}-${bldIndex}-renowire`, [exactT('PrivateProtectionsCredits', 'fullyUpdatedProperty10Years|1')])}
          <input
            type="number"
            className="form-control"
            value={bld.renoWiringYear}
            onChange={(e) => updateBuilding(locIndex, bldIndex, { renoWiringYear: e.target.value })}
          />
        </div>
        <div className="form-group">
          {labelWithRate('Reno: Heating Year', `mbfld-${locIndex}-${bldIndex}-renoheat`, [exactT('PrivateProtectionsCredits', 'fullyUpdatedProperty10Years|1')])}
          <input
            type="number"
            className="form-control"
            value={bld.renoHeatingYear}
            onChange={(e) => updateBuilding(locIndex, bldIndex, { renoHeatingYear: e.target.value })}
          />
        </div>
        <div className="form-group">
          {labelWithRate('Reno: Roofing Year', `mbfld-${locIndex}-${bldIndex}-renoroof`, [sectionT('Roofreplaced')])}
          <input
            type="number"
            className="form-control"
            value={bld.renoRoofingYear}
            onChange={(e) => updateBuilding(locIndex, bldIndex, { renoRoofingYear: e.target.value })}
          />
        </div>
      </div>

      <h5
        style={{ marginTop: '10px', scrollMarginTop: '90px', ...(flashOrigin === `mbfld-${locIndex}-${bldIndex}-exp` ? { background: '#fff3b0', borderRadius: '3px' } : null) }}
        id={`mbfld-${locIndex}-${bldIndex}-exp`}
      >
        Exposures
        <button
          type="button"
          onClick={() => showAndHighlight([sectionT('FireMod')], `mbfld-${locIndex}-${bldIndex}-exp`)}
          title="Show the Fire Mod rate table these exposures affect"
          style={sourceBtnStyle}
        >
          ?
        </button>
      </h5>
      <div className="grid">
        <div className="form-group">
          <label style={{ fontWeight: 'normal' }}>
            <input
              type="checkbox"
              checked={bld.expShopCarpentry}
              onChange={(e) => updateBuilding(locIndex, bldIndex, { expShopCarpentry: e.target.checked })}
            />{' '}
            Shop Carpentry / Woodworking
          </label>
          <label style={{ fontWeight: 'normal', display: 'block' }}>
            <input
              type="checkbox"
              checked={bld.expAutoRepair}
              onChange={(e) => updateBuilding(locIndex, bldIndex, { expAutoRepair: e.target.checked })}
            />{' '}
            Automotive Repair
          </label>
          <label style={{ fontWeight: 'normal', display: 'block' }}>
            <input
              type="checkbox"
              checked={bld.expAutoPaint}
              onChange={(e) => updateBuilding(locIndex, bldIndex, { expAutoPaint: e.target.checked })}
            />{' '}
            Automotive Paint & Body
          </label>
          <label style={{ fontWeight: 'normal', display: 'block' }}>
            <input
              type="checkbox"
              checked={bld.expWelding}
              onChange={(e) => updateBuilding(locIndex, bldIndex, { expWelding: e.target.checked })}
            />{' '}
            Welding
          </label>
          <label style={{ fontWeight: 'normal', display: 'block' }}>
            <input
              type="checkbox"
              checked={bld.expManufacturing}
              onChange={(e) => updateBuilding(locIndex, bldIndex, { expManufacturing: e.target.checked })}
            />{' '}
            Manufacturing
          </label>
        </div>
        <div className="form-group">
          <label style={{ fontWeight: 'normal' }}>
            <input
              type="checkbox"
              checked={bld.expCommercialCooking}
              onChange={(e) => updateBuilding(locIndex, bldIndex, { expCommercialCooking: e.target.checked })}
            />{' '}
            Commercial Cooking
          </label>
          {bld.expCommercialCooking && (
            <div style={{ paddingLeft: '20px' }}>
              <label style={{ fontWeight: 'normal', display: 'block' }}>
                <input
                  type="checkbox"
                  checked={bld.cookFryer}
                  onChange={(e) => updateBuilding(locIndex, bldIndex, { cookFryer: e.target.checked })}
                />{' '}
                Fryer
              </label>
              <label style={{ fontWeight: 'normal', display: 'block' }}>
                <input
                  type="checkbox"
                  checked={bld.cookGrill}
                  onChange={(e) => updateBuilding(locIndex, bldIndex, { cookGrill: e.target.checked })}
                />{' '}
                Grill
              </label>
              <label style={{ fontWeight: 'normal', display: 'block' }}>
                <input
                  type="checkbox"
                  checked={bld.cookOpenFlame}
                  onChange={(e) => updateBuilding(locIndex, bldIndex, { cookOpenFlame: e.target.checked })}
                />{' '}
                Open Flame
              </label>
              <label style={{ fontWeight: 'normal', display: 'block' }}>
                <input
                  type="checkbox"
                  checked={bld.cookOther}
                  onChange={(e) => updateBuilding(locIndex, bldIndex, { cookOther: e.target.checked })}
                />{' '}
                Other
              </label>
            </div>
          )}
        </div>
        <div className="form-group">
          {labelWithRate('% of Building Inhabited (Cooking)', `mbfld-${locIndex}-${bldIndex}-cookpbi`, [exactT('FireMod', `Cooking_PrcBuildingInhabited|${bld.cookingPBI}`), sectionT('FireMod')])}
          <select
            className="form-control"
            value={bld.cookingPBI}
            onChange={(e) => updateBuilding(locIndex, bldIndex, { cookingPBI: e.target.value })}
          >
            {MB_COOKING_PBI.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
      </div>

      <h5 style={{ marginTop: '10px' }}>Property Coverages</h5>
      {bld.coverages.map((cov, covIndex) =>
        renderCoverageForm(cov, locIndex, bldIndex, covIndex)
      )}
      <button
        type="button"
        className="btn btn-secondary"
        onClick={() => addCoverage(locIndex, bldIndex)}
      >
        + Add Coverage
      </button>
    </div>
  );

  const renderLocationForm = (loc, locIndex) => (
    <div key={locIndex} className="card">
      <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <h3>Location {locIndex + 1}</h3>
        <button
          type="button"
          className="btn btn-danger"
          onClick={() => removeLocation(locIndex)}
          disabled={formData.locations.length <= 1}
        >
          Remove Location
        </button>
      </div>

      <div className="grid">
        <div className="form-group">
          <label>Name</label>
          <input
            type="text"
            className="form-control"
            value={loc.name}
            onChange={(e) => updateLocation(locIndex, { name: e.target.value })}
          />
        </div>
        <div className="form-group">
          {labelWithRate('County', `mbfld-${locIndex}-county`, [sectionT('WindBaseRate')])}
          <SearchableSelect
            options={countyOptions}
            value={loc.county}
            onChange={(value) => updateLocation(locIndex, { county: value })}
            placeholder="Select county..."
            required
          />
        </div>
        <div className="form-group">
          <label>Location flags</label>
          {checkboxWithRate(
            'Metropolitan Area (TRIA)',
            loc.isMetropolitan,
            (e) => updateLocation(locIndex, { isMetropolitan: e.target.checked }),
            `mbfld-${locIndex}-metro`,
            [sectionT('lineTRIA')]
          )}
          <label style={{ fontWeight: 'normal', display: 'block', marginTop: '4px' }}>
            <input
              type="checkbox"
              checked={loc.inspection}
              onChange={(e) => updateLocation(locIndex, { inspection: e.target.checked })}
            />{' '}
            Inspection Required
          </label>
        </div>
      </div>

      {loc.buildings.map((bld, bldIndex) => renderBuildingForm(bld, locIndex, bldIndex))}
      <button
        type="button"
        className="btn btn-secondary"
        onClick={() => addBuilding(locIndex)}
      >
        + Add Building
      </button>
    </div>
  );

  // ------------------------------------------------------------------

  return (
    <div>
      <h1>Mercantile Brace Calculator</h1>
      <p className="text-muted">
        X-Wind Commercial Property premium calculator (polunio16, mercantilebrace)
      </p>

      <TemplateManager
        currentData={{ ...formData, versionNumber }}
        onLoadTemplate={handleLoadTemplate}
        templateType="mercantile-brace"
      />

      <form onSubmit={handleSubmit}>
        <div className="card">
          <h2>Configuration</h2>
          <div className="grid">
            <div className="form-group">
              <label>API Version Number</label>
              <input
                type="text"
                className="form-control"
                value={versionNumber}
                onChange={(e) => setVersionNumber(e.target.value)}
                placeholder="e.g., 1.0.0"
                pattern="^\d+\.\d+\.\d+(-.*)?$"
                title="Version must be in format X.Y.Z or X.Y.Z-label"
              />
              <small className="text-muted">
                1.0.0 — options mercantilebrace__1_0_0, 1.0.1 — options mercantilebrace__1_0_1
              </small>
            </div>
            <div className="form-group">
              <label>&nbsp;</label>
              <div className="flex">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleDownloadSettingsXlsx}
                  disabled={downloadingXlsx || !versionNumber}
                >
                  {downloadingXlsx ? 'Downloading...' : 'Download Settings (XLSX)'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleToggleRateTables}
                  disabled={rateTablesLoading || !versionNumber}
                >
                  {rateTablesLoading
                    ? 'Loading...'
                    : showRateTables ? 'Hide Rate Tables' : 'Show Rate Tables'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {showRateTables && rateTables?.data && (
          <div className="card">
            <h2>Rate Tables — {rateTables.version} ({formData.policy.state})</h2>
            <MercantileBraceRateTables
              settings={rateTables.data}
              state={formData.policy.state}
              highlightMatch={highlight ? (k) => (highlight.targets || []).some((t) => t.test && t.test(k)) : null}
            />
          </div>
        )}

        <div
          className="card"
          id="mbalgo"
          style={{ scrollMarginTop: '90px', ...(flashOrigin === 'mbalgo' ? { boxShadow: '0 0 0 3px #f0ad4e' } : null) }}
        >
          <details open={!!result}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '1.1em' }}>
              Calculation algorithm (step by step){result ? ' + worked example' : ''}
            </summary>
            <div style={{ marginTop: '12px' }}>
              <MercantileBraceAlgorithm
                result={result}
                onOpenTable={(section) => showAndHighlight([sectionT(section)], 'mbalgo')}
              />
            </div>
          </details>
        </div>

        {showRateTables && highlight?.originId && (
          <button
            type="button"
            onClick={backToResult}
            title="Scroll back to the factor you came from"
            style={{
              position: 'fixed',
              right: '24px',
              bottom: '24px',
              zIndex: 1000,
              padding: '10px 16px',
              borderRadius: '24px',
              border: 'none',
              background: '#0d6efd',
              color: '#fff',
              fontWeight: 'bold',
              boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
              cursor: 'pointer'
            }}
          >
            ↩ Back to result
          </button>
        )}

        <div className="card">
          <h2>Policy Information</h2>
          <div className="grid">
            <div className="form-group">
              <label>State</label>
              <select
                className="form-control"
                value={formData.policy.state}
                onChange={(e) => updatePolicy({ state: e.target.value })}
              >
                {PROPERTY_STATES.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Rate Type</label>
              <select
                className="form-control"
                value={formData.policy.rateType}
                onChange={(e) => updatePolicy({ rateType: e.target.value })}
              >
                {MB_RATE_TYPES.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Effective Date</label>
              <input
                type="date"
                className="form-control"
                value={formData.policy.effectiveDate}
                onChange={(e) => handleEffectiveDateChange(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Expiration Date</label>
              <input
                type="date"
                className="form-control"
                value={formData.policy.expirationDate}
                readOnly
                disabled
                style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
              />
              <small className="text-muted">Auto: Effective Date + 1 year</small>
            </div>
          </div>

          <div
            className="form-group"
            id="mbfld-tria"
            style={{ scrollMarginTop: '90px', ...(flashOrigin === 'mbfld-tria' ? { background: '#fff3b0', borderRadius: '3px' } : null) }}
          >
            <label style={{ fontWeight: 'normal' }}>
              <input
                type="checkbox"
                checked={formData.policy.triaProperty}
                onChange={(e) => updatePolicy({ triaProperty: e.target.checked })}
              />{' '}
              TRIA (Terrorism) coverage
            </label>
            <button
              type="button"
              onClick={() => showAndHighlight([sectionT('lineTRIA')], 'mbfld-tria')}
              title="Show the TRIA (Terrorism) rate table"
              style={sourceBtnStyle}
            >
              ?
            </button>
          </div>
        </div>

        <div className="card">
          <h2>Property Settings</h2>
          <div className="grid">
            <div className="form-group">
              <label>Underwriting</label>
              {checkboxWithRate(
                'High Crime Area (UW)',
                formData.policy.lineProperty.highCrimeArea,
                (e) => updatePolicySection('lineProperty', { highCrimeArea: e.target.checked }),
                'mbfld-highcrime',
                [exactT('PrivateProtectionsCredits', 'HighCrimeDebit|1')]
              )}
              {checkboxWithRate(
                'Has Loss History',
                formData.policy.lineProperty.isLossHist,
                (e) => updatePolicySection('lineProperty', { isLossHist: e.target.checked }),
                'mbfld-losshist',
                [exactT('PrivateProtectionsCredits', 'LossFreeCredit')],
                true
              )}
            </div>
            <div className="form-group">
              {labelWithRate('Last Property Loss Date', 'mbfld-lossdate', [exactT('PrivateProtectionsCredits', 'LossFreeCredit')])}
              <input
                type="date"
                className="form-control"
                value={formData.policy.lineProperty.lossHistoryDateLast}
                onChange={(e) => updatePolicySection('lineProperty', { lossHistoryDateLast: e.target.value })}
              />
            </div>
            <div className="form-group">
              {labelWithRate('Last Loss Date (All Lines)', 'mbfld-lossdateall', [exactT('PrivateProtectionsCredits', 'LossFreeCredit')])}
              <input
                type="date"
                className="form-control"
                value={formData.policy.lineProperty.lossHistoryDateLastAll}
                onChange={(e) => updatePolicySection('lineProperty', { lossHistoryDateLastAll: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Final Premium Credit</label>
              <input
                type="number"
                step="0.01"
                className="form-control"
                value={formData.policy.lineProperty.finalPremiumCredit}
                placeholder="e.g. 0.9 (10% credit)"
                onChange={(e) => updatePolicySection('lineProperty', { finalPremiumCredit: e.target.value })}
              />
              <small className="text-muted">Final credit on total property premium (no rate table)</small>
            </div>
          </div>
        </div>

        {formData.locations.map((loc, locIndex) => renderLocationForm(loc, locIndex))}
        <div className="form-group">
          <button type="button" className="btn btn-secondary" onClick={addLocation}>
            + Add Location
          </button>
        </div>

        <div className="card">
          <div className="flex">
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Calculating...' : 'Calculate Premium'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCalculateAndDownload}
              disabled={loading}
            >
              Calculate & Download (ZIP)
            </button>
          </div>
        </div>
      </form>

      {error && (
        <div className="card" style={{ backgroundColor: '#f8d7da', border: '1px solid #f5c6cb' }}>
          <h3 style={{ color: '#721c24' }}>Error</h3>
          <p style={{ color: '#721c24' }}>{error}</p>
        </div>
      )}

      {result && result._status === 'ok' && (
        <>
          {renderSummary()}
          {renderPropertyResults()}
          {renderTRIAResults()}
        </>
      )}

      {result && (
        <div className="card">
          <JsonViewer data={result} title="Response JSON" defaultCollapsed={true} />
        </div>
      )}

      {lastRequest && (
        <div className="card">
          <JsonViewer data={lastRequest} title="Request JSON" defaultCollapsed={true} />
        </div>
      )}
    </div>
  );
};

export default MercantileBrace;
